import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import api from "../../../api";
import { Loader } from "../loader";
import moment from "moment";
import { Pagination, Stack, Select, MenuItem } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import "./WaqarTradeMonitor.css";

const waqarTmSelectSx = {
  width: 280,
  maxWidth: "100%",
  background: "#ffffff",
  borderRadius: "14px",
  fontFamily: "'Space Grotesk', sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#10201b",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(16, 32, 27, 0.12)",
    borderRadius: "14px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(16, 32, 27, 0.22)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#14b8a6",
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    padding: "12px 16px",
  },
  "& .MuiSelect-icon": {
    color: "#0d9488",
  },
};

const waqarTmMenuProps = {
  PaperProps: {
    sx: {
      mt: 1,
      borderRadius: "14px",
      border: "1px solid rgba(16, 32, 27, 0.08)",
      boxShadow: "0 18px 44px rgba(16, 32, 27, 0.14)",
      "& .MuiMenuItem-root": {
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: 14,
        padding: "10px 16px",
        color: "#10201b",
      },
      "& .MuiMenuItem-root:hover": {
        background: "rgba(20, 184, 166, 0.1) !important",
      },
      "& .MuiMenuItem-root.Mui-selected": {
        background: "linear-gradient(90deg, #0d9488, #0284c7) !important",
        color: "#fff !important",
        fontWeight: 700,
      },
      "& .MuiMenuItem-root.Mui-selected:hover": {
        background: "linear-gradient(90deg, #0f766e, #0369a1) !important",
        color: "#fff !important",
      },
    },
  },
};

const fmt = (n, d = 2) => (n == null ? "—" : Number(n).toFixed(d));
const fmtPrice = (n) => (n == null ? "—" : `$${Number(n).toFixed(4)}`);

const NICKNAMES = { ben: "Ibad", david: "Shehenshah", max: "Haider" };
const getDisplayName = (s) => {
  const name = s.name || s.email || "";
  const lower = name.toLowerCase();
  for (const [key, nick] of Object.entries(NICKNAMES)) {
    if (lower.includes(key)) return `${nick} - ${name} `;
  }
  return name;
};

const cardStyle = (bg) => ({
  background: bg,
  borderRadius: 16,
  padding: "18px 20px",
  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  flex: "1 1 calc(25% - 11px)",
  minWidth: 160,
  maxWidth: "calc(25% - 11px)",
  boxSizing: "border-box",
});

const label = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1,
  textTransform: "uppercase",
  marginBottom: 6,
  opacity: 0.75,
};
const bigVal = (color) => ({
  fontSize: 20,
  fontWeight: 700,
  color,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const TH = ({ children, right }) => (
  <th
    style={{
      background: "#5e17eb",
      color: "#fff",
      padding: "10px 14px",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.8,
      textTransform: "uppercase",
      textAlign: right ? "right" : "left",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </th>
);
const TD = ({ children, right, style }) => (
  <td
    style={{
      padding: "9px 14px",
      fontSize: 13,
      textAlign: right ? "right" : "left",
      borderBottom: "1px solid #eee",
      ...style,
    }}
  >
    {children}
  </td>
);

const PositionBadge = ({ type }) => {
  const bg =
    type === "Long" ? "#d4f7e0" : type === "Short" ? "#fde8e8" : "#e8f0ff";
  const color =
    type === "Long" ? "#1a7a40" : type === "Short" ? "#c0392b" : "#3a5fc8";
  return (
    <span
      style={{
        background: bg,
        color,
        borderRadius: 6,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {type?.toUpperCase() || "—"}
    </span>
  );
};

const OutcomeBadge = ({ outcome }) => {
  const map = {
    "TP Hit": ["#d4f7e0", "#1a7a40"],
    "SL Hit": ["#fde8e8", "#c0392b"],
    Closed: ["#fff3cd", "#856404"],
  };
  const [bg, color] = map[outcome] || ["#e9ecef", "#555"];
  return (
    <span
      style={{
        background: bg,
        color,
        borderRadius: 6,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {outcome || "—"}
    </span>
  );
};

// ── Waqar-portal Trade Monitor presentation helpers (scoped to
//    <TradeMonitor/> below — TradeMonitorSelf keeps using the shared
//    TH/TD/PositionBadge/OutcomeBadge helpers above, untouched) ──
const waqarSourcePillMeta = (sourceType) => {
  switch (sourceType) {
    case "liveSession":
      return { cls: "waqar-tm-pill--live", text: "Live" };
    case "stock":
      return { cls: "waqar-tm-pill--stock", text: "Stock" };
    case "forex":
      return { cls: "waqar-tm-pill--forex", text: "Forex" };
    default:
      return { cls: "waqar-tm-pill--crypto", text: "Crypto" };
  }
};

const WaqarSourcePill = ({ sourceType }) => {
  const { cls, text } = waqarSourcePillMeta(sourceType);
  return <span className={`waqar-tm-pill ${cls}`}>{text}</span>;
};

const WaqarPositionPill = ({ type }) => {
  const t = (type || "").toLowerCase();
  const cls =
    t === "long"
      ? "waqar-tm-pill--long"
      : t === "short"
        ? "waqar-tm-pill--short"
        : t === "spot"
          ? "waqar-tm-pill--spot"
          : "waqar-tm-pill--neutral";
  return (
    <span className={`waqar-tm-pill ${cls}`}>{type?.toUpperCase() || "—"}</span>
  );
};

const WaqarOutcomePill = ({ outcome }) => {
  const cls =
    outcome === "TP Hit"
      ? "waqar-tm-pill--tp"
      : outcome === "SL Hit"
        ? "waqar-tm-pill--sl"
        : outcome === "Closed"
          ? "waqar-tm-pill--closed"
          : "waqar-tm-pill--neutral";
  return <span className={`waqar-tm-pill ${cls}`}>{outcome || "—"}</span>;
};

const generateReportHtml = (memberName, fromDate, toDate, stats, trades) => {
  const fmt2 = (n, d = 2) => (n == null ? "—" : Number(n).toFixed(d));
  const fmtP = (n) => (n == null ? "—" : `$${Number(n).toFixed(4)}`);
  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
  const outcomeColor = (o) =>
    o === "TP Hit" ? "#1a7a40" : o === "SL Hit" ? "#c0392b" : "#856404";
  const outcomeBg = (o) =>
    o === "TP Hit" ? "#d4f7e0" : o === "SL Hit" ? "#fde8e8" : "#fff3cd";

  const rows = trades
    .map(
      (t) => `
    <tr>
      <td>${t.coin || t.currency || "—"}</td>
      <td>${(t.sourceType || "post").charAt(0).toUpperCase() + (t.sourceType || "post").slice(1)}</td>
      <td>${t.positionType || (t.tradeType === "spot" ? "Spot" : "—")}</td>
      <td>${fmtP(t.entryPrice)}</td>
      <td>${t.closedPrice ? fmtP(t.closedPrice) : "—"}</td>
      <td><span style="background:${outcomeBg(t.outcome)};color:${outcomeColor(t.outcome)};padding:2px 8px;border-radius:4px;font-size:11px;font-weight:700">${t.outcome || "—"}</span></td>
      <td style="color:${(t.pnlUSDT || 0) >= 0 ? "#1a7a40" : "#c0392b"};font-weight:600">${t.pnlUSDT != null ? `${t.pnlUSDT >= 0 ? "+" : ""}${fmt2(t.pnlUSDT)} USDT (${fmt2(t.roiPercent)}%)` : "—"}</td>
      <td>$${fmt2(t.balanceUsed)}${t.tradeType === "futures" && t.leverage > 1 ? ` ${t.leverage}x` : ""}</td>
      <td style="color:#666;font-size:11px">${fmtDate(t.createdAt)}</td>
      <td style="color:#666;font-size:11px">${fmtDate(t.resolvedAt)}</td>
    </tr>`,
    )
    .join("");

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Trade Report</title>
  <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=JetBrains+Mono:wght@500;700&display=swap" rel="stylesheet">
  <style>
    body{font-family:'Space Grotesk',sans-serif;margin:0;padding:28px 32px;color:#10201b;background:#f4f6f5}
    .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding:22px 24px;background:#fff;border-radius:18px;box-shadow:0 4px 18px rgba(13,26,22,0.08)}
    .title{font-size:20px;font-weight:700;color:#0d1a16}
    .meta{font-family:'JetBrains Mono',monospace;font-size:10px;letter-spacing:0.14em;text-transform:uppercase;color:#6b7d77;margin-top:5px}
    .stats-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:20px}
    .stat{background:#fff;border:1px solid rgba(16,32,27,0.08);border-radius:16px;padding:16px 18px;box-shadow:0 2px 10px rgba(13,26,22,0.05)}
    .stat-label{font-family:'JetBrains Mono',monospace;font-size:9px;font-weight:700;letter-spacing:0.2em;text-transform:uppercase;color:#6b7d77;margin-bottom:6px}
    .stat-value{font-family:'JetBrains Mono',monospace;font-size:22px;font-weight:700;color:#0d1a16}
    .stat-value.green{color:#047857}.stat-value.red{color:#dc2626}
    table{width:100%;border-collapse:collapse;font-size:12px;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 2px 10px rgba(13,26,22,0.05)}
    thead tr{background:linear-gradient(90deg,#0d9488,#0284c7)}
    th{color:#fff;padding:11px 12px;text-align:left;font-family:'JetBrains Mono',monospace;font-size:9px;font-weight:700;letter-spacing:0.18em;text-transform:uppercase;white-space:nowrap}
    td{padding:10px 12px;border-bottom:1px solid rgba(16,32,27,0.06);color:#3f4f4a}
    tr:nth-child(even) td{background:rgba(20,184,166,0.04)}
    .footer{margin-top:16px;font-family:'JetBrains Mono',monospace;font-size:9px;letter-spacing:0.1em;text-transform:uppercase;color:#8d9b96;text-align:right}
    @media print{body{padding:0;background:#fff}.no-print{display:none}}
  </style></head><body>
  <div class="header">
    <div>
      <div class="title">Trade Report — ${memberName}</div>
      <div class="meta">Period: ${fromDate} &nbsp;→&nbsp; ${toDate} &nbsp;·&nbsp; Generated: ${new Date().toLocaleString()}</div>
    </div>
    <button class="no-print" onclick="window.print()" style="background:linear-gradient(120deg,#0d9488,#0284c7);color:#fff;border:none;border-radius:10px;padding:10px 20px;font-family:'Space Grotesk',sans-serif;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 4px 14px rgba(13,148,136,0.3)">Print / Save PDF</button>
  </div>
  <div class="stats-grid">
    <div class="stat"><div class="stat-label">Total Trades</div><div class="stat-value">${stats.total}</div></div>
    <div class="stat"><div class="stat-label">TP Hit</div><div class="stat-value green">${stats.tpHit}</div></div>
    <div class="stat"><div class="stat-label">SL Hit</div><div class="stat-value red">${stats.slHit}</div></div>
    <div class="stat"><div class="stat-label">Timeout / Manual Close</div><div class="stat-value">${stats.timeout + stats.closed}</div></div>
    <div class="stat"><div class="stat-label">Win Rate</div><div class="stat-value ${parseFloat(stats.winRate) >= 50 ? "green" : "red"}">${stats.winRate}%</div></div>
    <div class="stat"><div class="stat-label">Total P&L</div><div class="stat-value ${stats.totalPnl >= 0 ? "green" : "red"}">${stats.totalPnl >= 0 ? "+" : ""}$${fmt2(stats.totalPnl)}</div></div>
  </div>
  <table>
    <thead><tr><th>Symbol</th><th>Source</th><th>Position</th><th>Entry</th><th>Exit</th><th>Outcome</th><th>P&amp;L</th><th>Size</th><th>Opened</th><th>Closed</th></tr></thead>
    <tbody>${rows || "<tr><td colspan='10' style='text-align:center;color:#999;padding:20px'>No trades in this period</td></tr>"}</tbody>
  </table>
  <div class="footer">TenUp Trade Monitor · Exported ${new Date().toLocaleString()}</div>
  </body></html>`;
};

export const TradeMonitor = () => {
  const [loader, setLoader] = useState(false);
  const [supportList, setSupportList] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [portfolio, setPortfolio] = useState(null);
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [totalUnrealized, setTotalUnrealized] = useState(0);
  const [closedPage, setClosedPage] = useState(1);
  const [closedTotalPages, setClosedTotalPages] = useState(1);
  const [deletingId, setDeletingId] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    api
      .getSupportAdmin(false)
      .then((res) => {
        if (res?.success) setSupportList(res.support || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedId) return;
    loadAll(selectedId);

    const interval = setInterval(() => loadAll(selectedId), 30000);
    return () => clearInterval(interval);
  }, [selectedId]);

  const loadAll = async (adminId, page = 1) => {
    setLoader(true);
    setPortfolio(null);
    setOpenTrades([]);
    setClosedTrades([]);
    setTotalUnrealized(0);
    setClosedPage(1);
    setClosedTotalPages(1);
    try {
      const [portRes, liveRes, closedRes] = await Promise.all([
        api.getSupportPortfolio(adminId),
        api.getSupportLiveTrades(adminId),
        api.getSupportTrades(adminId, "Closed", 1),
      ]);
      if (portRes?.success) setPortfolio(portRes.portfolio);
      if (liveRes?.success) {
        setOpenTrades(liveRes.trades || []);
        setTotalUnrealized(liveRes.totalUnrealizedPnl || 0);
      }
      if (closedRes?.success) {
        setClosedTrades(closedRes.trades || []);
        setClosedTotalPages(closedRes.totalPages || 1);
      }
    } catch (e) {
      console.error(e);
    }
    setLoader(false);
  };

  const loadClosedPage = async (adminId, page, outcome = outcomeFilter) => {
    try {
      const res = await api.getSupportTrades(adminId, "Closed", page, outcome);
      if (res?.success) {
        setClosedTrades(res.trades || []);
        setClosedTotalPages(res.totalPages || 1);
        setClosedPage(page);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleOutcomeFilter = (val) => {
    setOutcomeFilter(val);
    if (selectedId) loadClosedPage(selectedId, 1, val);
  };

  const handleCloseTrade = async (entryId, currentPrice, symbol) => {
    if (
      !window.confirm(
        `Close ${symbol} at $${currentPrice}?\n\nP&L will be calculated at the current price and portfolio updated.`,
      )
    )
      return;
    try {
      setClosingId(entryId);
      const res = await api.closeSupportTradeAtMarket(entryId, currentPrice);
      if (res?.success) loadAll(selectedId, closedPage);
    } catch (e) {
      console.error("[closeTrade]", e);
      alert("Failed to close trade. Please try again.");
    } finally {
      setClosingId(null);
    }
  };

  const handleDeleteTrade = async (entryId, symbol, status) => {
    const label = status === "Open" ? "open" : "closed";
    if (
      !window.confirm(
        `Delete this ${label} trade (${symbol})?\n\nBalance used will be fully restored to the member's portfolio.`,
      )
    )
      return;
    try {
      setDeletingId(entryId);
      const res = await api.deleteSupportTradeEntry(entryId);
      if (res?.success) loadAll(selectedId, closedPage);
    } catch (e) {
      console.error("[deleteTrade]", e);
      alert("Failed to delete trade. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleGenerateReport = async () => {
    if (!reportFrom || !reportTo) {
      alert("Please select both From and To dates.");
      return;
    }
    if (new Date(reportFrom) > new Date(reportTo)) {
      alert("From date must be before To date.");
      return;
    }
    const diffDays =
      (new Date(reportTo) - new Date(reportFrom)) / (1000 * 60 * 60 * 24);
    if (diffDays > 31) {
      alert("Date range cannot exceed 31 days. Please narrow your selection.");
      return;
    }
    setReportLoading(true);
    try {
      const res = await api.getSupportTradeReport(
        selectedId,
        reportFrom,
        reportTo,
      );
      if (!res?.success) {
        alert(res?.message || "Failed to generate report.");
        return;
      }
      const memberName = getDisplayName(
        supportList.find((s) => s._id === selectedId) || {},
      );
      const html = generateReportHtml(
        memberName,
        reportFrom,
        reportTo,
        res.stats,
        res.trades,
      );
      const win = window.open("", "_blank");
      if (win) {
        win.document.write(html);
        win.document.close();
      } else {
        alert("Pop-up blocked. Please allow pop-ups for this site.");
      }
      setShowReportModal(false);
    } catch (e) {
      console.error("[generateReport]", e);
      alert("Failed to generate report.");
    } finally {
      setReportLoading(false);
    }
  };

  // Derived stats
  const winRate = (() => {
    if (!portfolio) return 0;
    const settled = portfolio.winTrades + portfolio.lossTrades;
    return settled === 0
      ? 0
      : ((portfolio.winTrades / settled) * 100).toFixed(1);
  })();

  const breakEvenRate = (() => {
    if (!portfolio) return 0;
    const avgWin = portfolio.avgWinUSDT || 0;
    const avgLoss = Math.abs(portfolio.avgLossUSDT || 0);
    const denom = avgWin + avgLoss;
    return denom === 0 ? 0 : ((avgLoss / denom) * 100).toFixed(1);
  })();

  const belowBreakEven = portfolio
    ? (parseFloat(breakEvenRate) - parseFloat(winRate)).toFixed(1)
    : 0;

  return (
    <div className="waqar-tm">
      {/* Report Modal */}
      {showReportModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(13,26,22,0.55)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 22,
              padding: "30px 32px",
              width: 400,
              boxShadow: "0 32px 80px rgba(13,26,22,0.22)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 24,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 12,
                  background: "linear-gradient(135deg,#14b8a6,#0284c7)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: "#0d1a16",
                    lineHeight: 1.2,
                  }}
                >
                  Generate Trade Report
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.14em",
                    color: "#6b7d77",
                    textTransform: "uppercase",
                    marginTop: 2,
                  }}
                >
                  Select date range
                </div>
              </div>
            </div>

            {/* From Date */}
            <div style={{ marginBottom: 14 }}>
              <label
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  color: "#6b7d77",
                  textTransform: "uppercase",
                  display: "block",
                  marginBottom: 7,
                }}
              >
                From Date
              </label>
              <input
                type="date"
                value={reportFrom}
                onChange={(e) => setReportFrom(e.target.value)}
                style={{
                  width: "100%",
                  padding: "11px 14px",
                  borderRadius: 12,
                  border: "1.5px solid rgba(16,32,27,0.12)",
                  fontSize: 14,
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 600,
                  color: "#0d1a16",
                  boxSizing: "border-box",
                  outline: "none",
                  background: "rgba(20,184,166,0.04)",
                }}
              />
            </div>

            {/* To Date */}
            <div style={{ marginBottom: 26 }}>
              <label
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 9.5,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  color: "#6b7d77",
                  textTransform: "uppercase",
                  display: "block",
                  marginBottom: 7,
                }}
              >
                To Date
              </label>
              <input
                type="date"
                value={reportTo}
                onChange={(e) => setReportTo(e.target.value)}
                style={{
                  width: "100%",
                  padding: "11px 14px",
                  borderRadius: 12,
                  border: "1.5px solid rgba(16,32,27,0.12)",
                  fontSize: 14,
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 600,
                  color: "#0d1a16",
                  boxSizing: "border-box",
                  outline: "none",
                  background: "rgba(20,184,166,0.04)",
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => setShowReportModal(false)}
                style={{
                  flex: 1,
                  padding: "11px",
                  borderRadius: 12,
                  border: "1.5px solid rgba(16,32,27,0.12)",
                  background: "#fff",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 13,
                  color: "#3f4f4a",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateReport}
                disabled={reportLoading}
                style={{
                  flex: 2,
                  padding: "11px",
                  borderRadius: 12,
                  border: "none",
                  background: reportLoading
                    ? "rgba(13,148,136,0.5)"
                    : "linear-gradient(120deg,#0d9488,#0284c7)",
                  color: "#fff",
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: reportLoading ? "not-allowed" : "pointer",
                  letterSpacing: "0.02em",
                }}
              >
                {reportLoading ? "Generating…" : "Generate Report"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selector */}
      <div className="waqar-tm-selector-row">
        <Select
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          displayEmpty
          IconComponent={KeyboardArrowDownRoundedIcon}
          MenuProps={waqarTmMenuProps}
          sx={waqarTmSelectSx}
        >
          <MenuItem value="">
            <span style={{ color: "#8d9b96" }}>— Select Support Member —</span>
          </MenuItem>
          {supportList
            .filter((s) => {
              const n = (s.name || s.email || "").toLowerCase();
              return ["royala", "max", "ben", "david", "kate"].some((k) =>
                n.includes(k),
              );
            })
            .map((s) => (
              <MenuItem key={s._id} value={s._id}>
                {getDisplayName(s)}
              </MenuItem>
            ))}
        </Select>
        {selectedId && (
          <span className="waqar-tm-live-note">
            <span className="waqar-tm-live-dot" />
            auto-refreshes every 30s
          </span>
        )}
        {selectedId && (
          <button
            onClick={() => setShowReportModal(true)}
            style={{
              marginLeft: "auto",
              background: "linear-gradient(120deg,#0d9488,#0284c7)",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              padding: "11px 20px",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
              whiteSpace: "nowrap",
              letterSpacing: "0.02em",
              boxShadow: "0 4px 14px rgba(13,148,136,0.3)",
            }}
          >
            Generate Report
          </button>
        )}
      </div>

      {loader && <Loader />}

      {!selectedId && !loader && (
        <div className="waqar-tm-empty">
          Select a support member to view their trade performance.
        </div>
      )}

      {selectedId && !loader && (
        <>
          {/* ── Stat Cards ── */}
          <div className="waqar-tm-stats-grid">
            <div className="waqar-tm-stat-card waqar-tm-stat--balance">
              <div className="waqar-tm-stat-label">Balance</div>
              <div className="waqar-tm-stat-value">
                ${fmt(portfolio?.currentBalance ?? 0)}
              </div>
            </div>

            <div
              className={`waqar-tm-stat-card ${
                totalUnrealized >= 0
                  ? "waqar-tm-stat--unrealized-up"
                  : "waqar-tm-stat--unrealized-down"
              }`}
            >
              <div className="waqar-tm-stat-label">Unrealized P&L</div>
              <div className="waqar-tm-stat-value">
                {totalUnrealized >= 0 ? "+" : ""}${fmt(totalUnrealized)}
              </div>
            </div>

            <div
              className={`waqar-tm-stat-card ${
                (portfolio?.totalPnlUSDT ?? 0) >= 0
                  ? "waqar-tm-stat--realized-up"
                  : "waqar-tm-stat--realized-down"
              }`}
            >
              <div className="waqar-tm-stat-label">Realized P&L</div>
              <div className="waqar-tm-stat-value">
                {(portfolio?.totalPnlUSDT ?? 0) >= 0 ? "+" : ""}$
                {fmt(portfolio?.totalPnlUSDT ?? 0)}
              </div>
            </div>

            <div className="waqar-tm-stat-card waqar-tm-stat--open">
              <div className="waqar-tm-stat-label">Open Trades</div>
              <div className="waqar-tm-stat-value">{openTrades.length}</div>
            </div>

            <div className="waqar-tm-stat-card waqar-tm-stat--winrate">
              <div className="waqar-tm-stat-label">Win Rate</div>
              <div className="waqar-tm-stat-value">{winRate}%</div>
            </div>

            <div className="waqar-tm-stat-card waqar-tm-stat--wlt">
              <div className="waqar-tm-stat-label">
                Wins / Losses / Timeouts
              </div>
              <div className="waqar-tm-wlt">
                <span className="w">{portfolio?.winTrades ?? 0}W</span>
                <span className="sep">/</span>
                <span className="l">{portfolio?.lossTrades ?? 0}L</span>
                <span className="sep">/</span>
                <span className="t">{portfolio?.timeoutTrades ?? 0}T</span>
              </div>
            </div>

            <div className="waqar-tm-stat-card waqar-tm-stat--breakeven">
              <div className="waqar-tm-stat-label">Break-even Win Rate</div>
              <div className="waqar-tm-stat-value">{breakEvenRate}%</div>
              <div className="waqar-tm-stat-sub">
                avg win ${fmt(portfolio?.avgWinUSDT ?? 0)} · avg loss $
                {fmt(Math.abs(portfolio?.avgLossUSDT ?? 0))}
              </div>
              {belowBreakEven > 0 && (
                <div className="waqar-tm-stat-sub">
                  you are <strong>{belowBreakEven} pts</strong> below it
                </div>
              )}
            </div>

            <div className="waqar-tm-stat-card waqar-tm-stat--starting">
              <div className="waqar-tm-stat-label">Starting Balance</div>
              <div className="waqar-tm-stat-value">
                ${fmt(portfolio?.allocatedBalance ?? 0)}
              </div>
            </div>

            <div className="waqar-tm-stat-card waqar-tm-stat--open">
              <div className="waqar-tm-stat-label">Closed Trades</div>
              <div className="waqar-tm-stat-value">
                {(portfolio?.winTrades ?? 0) +
                  (portfolio?.lossTrades ?? 0) +
                  (portfolio?.timeoutTrades ?? 0)}
              </div>
            </div>
            <div className="waqar-tm-stat-card waqar-tm-stat--open">
              <div className="waqar-tm-stat-label">Total Trades</div>
              <div className="waqar-tm-stat-value">
                {portfolio?.totalTrades ?? 0}
              </div>
            </div>
          </div>

          {/* ── Open Trades ── */}
          <div className="waqar-tm-section">
            <div className="waqar-tm-table-card">
              <div className="waqar-tm-section-header">
                <span className="waqar-tm-section-title">Open Trades</span>
                <span className="waqar-tm-count-badge">
                  {openTrades.length}
                </span>
              </div>
              <div className="waqar-tm-table-scroll">
                <table className="waqar-tm-table">
                  <thead>
                    <tr className="waqar-tm-thead-row">
                      <th className="waqar-tm-th">Symbol</th>
                      <th className="waqar-tm-th">Source</th>
                      <th className="waqar-tm-th">Position</th>
                      <th className="waqar-tm-th">Entry</th>
                      <th className="waqar-tm-th">Target (TP)</th>
                      <th className="waqar-tm-th">Stop (SL)</th>
                      <th className="waqar-tm-th">Current Price</th>
                      <th className="waqar-tm-th waqar-tm-th--right">
                        Unrealized P&L
                      </th>
                      <th className="waqar-tm-th waqar-tm-th--right">Size</th>
                      <th className="waqar-tm-th">Opened</th>
                      <th className="waqar-tm-th"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {openTrades.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="waqar-tm-empty-row">
                          No open trades
                        </td>
                      </tr>
                    ) : (
                      openTrades.map((t) => (
                        <tr key={t._id} className="waqar-tm-row">
                          <td className="waqar-tm-td">
                            <span className="waqar-tm-symbol">
                              {t.coin || t.currency || "—"}
                            </span>
                          </td>
                          <td className="waqar-tm-td">
                            <WaqarSourcePill sourceType={t.sourceType} />
                          </td>
                          <td className="waqar-tm-td">
                            <WaqarPositionPill
                              type={
                                t.positionType ||
                                (t.tradeType === "spot" ? "Spot" : null)
                              }
                            />
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono">
                            {fmtPrice(t.entryPrice)}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono waqar-tm-tp">
                            {fmtPrice(t.takeProfit)}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono waqar-tm-sl">
                            {t.stopLoss ? fmtPrice(t.stopLoss) : "—"}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono">
                            {t.currentPrice ? (
                              fmtPrice(t.currentPrice)
                            ) : (
                              <span className="waqar-tm-muted">Loading…</span>
                            )}
                          </td>
                          <td className="waqar-tm-td waqar-tm-td--right">
                            {t.unrealizedPnl != null ? (
                              <>
                                <span
                                  className={
                                    t.unrealizedPnl >= 0
                                      ? "waqar-tm-pnl-up"
                                      : "waqar-tm-pnl-down"
                                  }
                                >
                                  {t.unrealizedPnl >= 0 ? "+" : ""}
                                  {fmt(t.unrealizedPnl)} USDT (
                                  {fmt(t.unrealizedRoi)}%)
                                </span>
                                {t.unrealizedRoi >= 0 && t.currentPrice && (
                                  <div>
                                    <button
                                      className="waqar-tm-close-btn"
                                      onClick={() =>
                                        handleCloseTrade(
                                          t._id,
                                          t.currentPrice,
                                          t.coin || t.currency || "?",
                                        )
                                      }
                                      disabled={closingId === t._id}
                                    >
                                      {closingId === t._id
                                        ? "Closing…"
                                        : "Close Trade"}
                                    </button>
                                  </div>
                                )}
                              </>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="waqar-tm-td waqar-tm-td--right waqar-tm-mono">
                            ${fmt(t.balanceUsed)}
                            {t.tradeType === "futures" && t.leverage > 1 && (
                              <span className="waqar-tm-leverage">
                                {t.leverage}x
                              </span>
                            )}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono waqar-tm-muted">
                            {moment(t.createdAt).format("M/D/YYYY, h:mm:ss A")}
                          </td>
                          <td className="waqar-tm-td">
                            <button
                              className="waqar-tm-del-btn"
                              onClick={() =>
                                handleDeleteTrade(
                                  t._id,
                                  t.coin || t.currency || "?",
                                  "Open",
                                )
                              }
                              disabled={deletingId === t._id}
                            >
                              {deletingId === t._id ? "…" : "✕"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── Closed Trades ── */}
          <div className="waqar-tm-section">
            <div className="waqar-tm-table-card">
              <div className="waqar-tm-section-header">
                <span className="waqar-tm-section-title">Closed Trades</span>
                <span className="waqar-tm-count-badge">
                  {closedTrades.length}
                </span>
                <select
                  value={outcomeFilter}
                  onChange={(e) => handleOutcomeFilter(e.target.value)}
                  style={{
                    marginLeft: "auto",
                    padding: "4px 10px",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  <option value="">All Outcomes</option>
                  <option value="TP Hit">TP Hit</option>
                  <option value="SL Hit">SL Hit</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div className="waqar-tm-table-scroll">
                <table className="waqar-tm-table">
                  <thead>
                    <tr className="waqar-tm-thead-row">
                      <th className="waqar-tm-th">Symbol</th>
                      <th className="waqar-tm-th">Source</th>
                      <th className="waqar-tm-th">Position</th>
                      <th className="waqar-tm-th">Entry</th>
                      <th className="waqar-tm-th">Exit</th>
                      <th className="waqar-tm-th">Outcome</th>
                      <th className="waqar-tm-th waqar-tm-th--right">P&L</th>
                      <th className="waqar-tm-th waqar-tm-th--right">Size</th>
                      <th className="waqar-tm-th">Opened</th>
                      <th className="waqar-tm-th">Closed</th>
                      <th className="waqar-tm-th"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {closedTrades.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="waqar-tm-empty-row">
                          No closed trades
                        </td>
                      </tr>
                    ) : (
                      closedTrades.map((t) => (
                        <tr key={t._id} className="waqar-tm-row">
                          <td className="waqar-tm-td">
                            <span className="waqar-tm-symbol">
                              {t.coin || t.currency || "—"}
                            </span>
                          </td>
                          <td className="waqar-tm-td">
                            <WaqarSourcePill sourceType={t.sourceType} />
                          </td>
                          <td className="waqar-tm-td">
                            <WaqarPositionPill
                              type={
                                t.positionType ||
                                (t.tradeType === "spot" ? "Spot" : null)
                              }
                            />
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono">
                            {fmtPrice(t.entryPrice)}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono">
                            {t.closedPrice ? fmtPrice(t.closedPrice) : "—"}
                          </td>
                          <td className="waqar-tm-td">
                            <WaqarOutcomePill outcome={t.outcome} />
                          </td>
                          <td className="waqar-tm-td waqar-tm-td--right">
                            {t.pnlUSDT != null ? (
                              <span
                                className={
                                  t.pnlUSDT >= 0
                                    ? "waqar-tm-pnl-up"
                                    : "waqar-tm-pnl-down"
                                }
                              >
                                {t.pnlUSDT >= 0 ? "+" : ""}
                                {fmt(t.pnlUSDT)} USDT ({fmt(t.roiPercent)}%)
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="waqar-tm-td waqar-tm-td--right waqar-tm-mono">
                            ${fmt(t.balanceUsed)}
                            {t.tradeType === "futures" && t.leverage > 1 && (
                              <span className="waqar-tm-leverage">
                                {t.leverage}x
                              </span>
                            )}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono waqar-tm-muted">
                            {moment(t.createdAt).format("M/D/YYYY, h:mm:ss A")}
                          </td>
                          <td className="waqar-tm-td waqar-tm-mono waqar-tm-muted">
                            {t.resolvedAt
                              ? moment(t.resolvedAt).format(
                                  "M/D/YYYY, h:mm:ss A",
                                )
                              : "—"}
                          </td>
                          <td className="waqar-tm-td">
                            <button
                              className="waqar-tm-del-btn"
                              onClick={() =>
                                handleDeleteTrade(
                                  t._id,
                                  t.coin || t.currency || "?",
                                  "Closed",
                                )
                              }
                              disabled={deletingId === t._id}
                            >
                              {deletingId === t._id ? "…" : "✕"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {closedTotalPages > 1 && (
                <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
                  <Pagination
                    count={closedTotalPages}
                    page={closedPage}
                    onChange={(_, v) => loadClosedPage(selectedId, v)}
                    color="primary"
                    size="small"
                  />
                </Stack>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── Support panel version — auto-loads with the logged-in member's own ID ──
export const TradeMonitorSelf = () => {
  const adminStore = useSelector((state) => state.adminReducer);
  const adminId = adminStore?.admin?.admin?._id;

  const [loader, setLoader] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [totalUnrealized, setTotalUnrealized] = useState(0);
  const [closedPage, setClosedPage] = useState(1);
  const [closedTotalPages, setClosedTotalPages] = useState(1);
  const [closingId, setClosingId] = useState(null);
  const [outcomeFilter, setOutcomeFilter] = useState("");

  useEffect(() => {
    if (!adminId) return;
    loadAll(adminId);
    const interval = setInterval(() => loadAll(adminId), 30000);
    return () => clearInterval(interval);
  }, [adminId]);

  const loadAll = async (id) => {
    setLoader(true);
    setPortfolio(null);
    setOpenTrades([]);
    setClosedTrades([]);
    setTotalUnrealized(0);
    setClosedPage(1);
    setClosedTotalPages(1);
    try {
      const [portRes, liveRes, closedRes] = await Promise.all([
        api.getSupportPortfolio(id),
        api.getSupportLiveTrades(id),
        api.getSupportTrades(id, "Closed", 1),
      ]);
      if (portRes?.success) setPortfolio(portRes.portfolio);
      if (liveRes?.success) {
        setOpenTrades(liveRes.trades || []);
        setTotalUnrealized(liveRes.totalUnrealizedPnl || 0);
      }
      if (closedRes?.success) {
        setClosedTrades(closedRes.trades || []);
        setClosedTotalPages(closedRes.totalPages || 1);
      }
    } catch (e) {
      console.error(e);
    }
    setLoader(false);
  };

  const loadClosedPage = async (page, outcome = outcomeFilter) => {
    try {
      const res = await api.getSupportTrades(adminId, "Closed", page, outcome);
      if (res?.success) {
        setClosedTrades(res.trades || []);
        setClosedTotalPages(res.totalPages || 1);
        setClosedPage(page);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleOutcomeFilter = (val) => {
    setOutcomeFilter(val);
    loadClosedPage(1, val);
  };

  const handleCloseTrade = async (entryId, currentPrice, symbol) => {
    if (
      !window.confirm(
        `Close trade for ${symbol} at current price $${currentPrice}?`,
      )
    )
      return;
    setClosingId(entryId);
    try {
      const res = await api.closeSupportTradeAtMarket(entryId, currentPrice);
      if (res?.success) loadAll(adminId);
      else alert(res?.message || "Failed to close trade");
    } catch (e) {
      alert("Error closing trade");
    }
    setClosingId(null);
  };

  const winRate = (() => {
    if (!portfolio) return 0;
    const settled = portfolio.winTrades + portfolio.lossTrades;
    return settled === 0
      ? 0
      : ((portfolio.winTrades / settled) * 100).toFixed(1);
  })();

  const breakEvenRate = (() => {
    if (!portfolio) return 0;
    const avgWin = portfolio.avgWinUSDT || 0;
    const avgLoss = Math.abs(portfolio.avgLossUSDT || 0);
    const denom = avgWin + avgLoss;
    return denom === 0 ? 0 : ((avgLoss / denom) * 100).toFixed(1);
  })();

  const belowBreakEven = portfolio
    ? (parseFloat(breakEvenRate) - parseFloat(winRate)).toFixed(1)
    : 0;

  if (!adminId) return null;

  return (
    <div style={{ padding: "0 15px" }}>
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 16 }}>
          My Trade Performance
        </span>
        <span style={{ fontSize: 11, color: "#aaa" }}>
          auto-refreshes every 30s
        </span>
      </div>

      {loader && <Loader />}

      {!loader && (
        <>
          {/* ── Stat Cards ── */}
          <div
            style={{
              display: "flex",
              gap: 14,
              flexWrap: "wrap",
              marginBottom: 28,
            }}
          >
            <div style={cardStyle("#f5f0ff")}>
              <div style={label}>Balance</div>
              <div style={bigVal("#3d0f8a")}>
                ${fmt(portfolio?.currentBalance ?? 0)}
              </div>
            </div>
            <div style={cardStyle("#fff0f0")}>
              <div style={label}>Unrealized P&L</div>
              <div style={bigVal(totalUnrealized >= 0 ? "#1a7a40" : "#c0392b")}>
                {totalUnrealized >= 0 ? "+" : ""}${fmt(totalUnrealized)}
              </div>
            </div>
            <div style={cardStyle("#fff0f0")}>
              <div style={label}>Realized P&L</div>
              <div
                style={bigVal(
                  (portfolio?.totalPnlUSDT ?? 0) >= 0 ? "#1a7a40" : "#c0392b",
                )}
              >
                {(portfolio?.totalPnlUSDT ?? 0) >= 0 ? "+" : ""}$
                {fmt(portfolio?.totalPnlUSDT ?? 0)}
              </div>
            </div>
            <div style={cardStyle("#fffde7")}>
              <div style={label}>Open Trades</div>
              <div style={bigVal("#e6a817")}>{openTrades.length}</div>
            </div>
            <div style={cardStyle("#f0fff4")}>
              <div style={label}>Win Rate</div>
              <div style={bigVal("#1a7a40")}>{winRate}%</div>
            </div>
            <div style={cardStyle("#f5f0ff")}>
              <div style={label}>Wins / Losses / Timeouts</div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ color: "#1a7a40" }}>
                  {portfolio?.winTrades ?? 0}W
                </span>
                <span style={{ color: "#aaa", margin: "0 4px" }}>/</span>
                <span style={{ color: "#c0392b" }}>
                  {portfolio?.lossTrades ?? 0}L
                </span>
                <span style={{ color: "#aaa", margin: "0 4px" }}>/</span>
                <span style={{ color: "#e6a817" }}>
                  {portfolio?.timeoutTrades ?? 0}T
                </span>
              </div>
            </div>
            <div style={cardStyle("#fff0f0")}>
              <div style={label}>Break-even Win Rate</div>
              <div style={bigVal("#c0392b")}>{breakEvenRate}%</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                avg win ${fmt(portfolio?.avgWinUSDT ?? 0)} · avg loss $
                {fmt(Math.abs(portfolio?.avgLossUSDT ?? 0))}
              </div>
              {belowBreakEven > 0 && (
                <div style={{ fontSize: 11, color: "#c0392b", marginTop: 2 }}>
                  you are <strong>{belowBreakEven} pts</strong> below it
                </div>
              )}
            </div>
            <div style={cardStyle("#f8f8f8")}>
              <div style={label}>Starting Balance</div>
              <div style={bigVal("#222")}>
                ${fmt(portfolio?.allocatedBalance ?? 0)}
              </div>
            </div>
            <div style={cardStyle("#eef3ff")}>
              <div style={label}>Closed Trades</div>
              <div style={bigVal("#3d0f8a")}>
                {(portfolio?.winTrades ?? 0) +
                  (portfolio?.lossTrades ?? 0) +
                  (portfolio?.timeoutTrades ?? 0)}
              </div>
            </div>
            <div style={cardStyle("#eef3ff")}>
              <div style={label}>Total Trades</div>
              <div style={bigVal("#3d0f8a")}>
                {portfolio?.totalTrades ?? 0}
              </div>
            </div>
          </div>

          {/* ── Open Trades ── */}
          <div style={{ marginBottom: 28 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <span
                style={{ fontWeight: 700, fontSize: 14, letterSpacing: 0.5 }}
              >
                OPEN TRADES
              </span>
              <span
                style={{
                  background: "#5e17eb",
                  color: "#fff",
                  borderRadius: 20,
                  padding: "2px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {openTrades.length}
              </span>
            </div>
            <div
              style={{
                overflowX: "auto",
                borderRadius: 10,
                boxShadow: "0 1px 8px rgba(0,0,0,0.08)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  background: "#fff",
                }}
              >
                <thead>
                  <tr>
                    <TH>Symbol</TH>
                    <TH>Source</TH>
                    <TH>Position</TH>
                    <TH>Entry</TH>
                    <TH>Target (TP)</TH>
                    <TH>Stop (SL)</TH>
                    <TH>Current Price</TH>
                    <TH right>Unrealized P&L</TH>
                    <TH right>Size</TH>
                    <TH>Opened</TH>
                  </tr>
                </thead>
                <tbody>
                  {openTrades.length === 0 ? (
                    <tr>
                      <td
                        colSpan={10}
                        style={{
                          textAlign: "center",
                          padding: 20,
                          color: "#aaa",
                        }}
                      >
                        No open trades
                      </td>
                    </tr>
                  ) : (
                    openTrades.map((t) => (
                      <tr key={t._id} style={{ background: "#fff" }}>
                        <TD>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>
                            {t.coin || t.currency || "—"}
                          </span>
                        </TD>
                        <TD>
                          <span
                            style={{
                              background:
                                t.sourceType === "liveSession"
                                  ? "#e8f4fd"
                                  : t.sourceType === "stock"
                                    ? "#e8f5e9"
                                    : t.sourceType === "forex"
                                      ? "#fff3e0"
                                      : "#f0f0ff",
                              color:
                                t.sourceType === "liveSession"
                                  ? "#1565c0"
                                  : t.sourceType === "stock"
                                    ? "#2e7d32"
                                    : t.sourceType === "forex"
                                      ? "#e65100"
                                      : "#5e17eb",
                              borderRadius: 6,
                              padding: "2px 8px",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {t.sourceType === "liveSession"
                              ? "Live"
                              : t.sourceType === "stock"
                                ? "Stock"
                                : t.sourceType === "forex"
                                  ? "Forex"
                                  : "Crypto"}
                          </span>
                        </TD>
                        <TD>
                          <PositionBadge
                            type={
                              t.positionType ||
                              (t.tradeType === "spot" ? "Spot" : null)
                            }
                          />
                        </TD>
                        <TD>{fmtPrice(t.entryPrice)}</TD>
                        <TD style={{ color: "#1a7a40" }}>
                          {fmtPrice(t.takeProfit)}
                        </TD>
                        <TD style={{ color: "#c0392b" }}>
                          {t.stopLoss ? fmtPrice(t.stopLoss) : "—"}
                        </TD>
                        <TD>
                          {t.currentPrice ? (
                            fmtPrice(t.currentPrice)
                          ) : (
                            <span style={{ color: "#aaa" }}>Loading…</span>
                          )}
                        </TD>
                        <TD right>
                          {t.unrealizedPnl != null ? (
                            <span
                              style={{
                                color:
                                  t.unrealizedPnl >= 0 ? "#1a7a40" : "#c0392b",
                                fontWeight: 600,
                              }}
                            >
                              {t.unrealizedPnl >= 0 ? "+" : ""}
                              {fmt(t.unrealizedPnl)} USDT (
                              {fmt(t.unrealizedRoi)}%)
                            </span>
                          ) : (
                            "—"
                          )}
                          {t.unrealizedRoi >= 5 && t.currentPrice && (
                            <div style={{ marginTop: 5 }}>
                              <button
                                onClick={() =>
                                  handleCloseTrade(
                                    t._id,
                                    t.currentPrice,
                                    t.coin || t.currency || "?",
                                  )
                                }
                                disabled={closingId === t._id}
                                style={{
                                  background: "#16a34a",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 6,
                                  padding: "3px 10px",
                                  fontSize: 11,
                                  fontWeight: 700,
                                  cursor: "pointer",
                                  opacity: closingId === t._id ? 0.6 : 1,
                                }}
                              >
                                {closingId === t._id
                                  ? "Closing…"
                                  : "Close Trade"}
                              </button>
                            </div>
                          )}
                        </TD>
                        <TD right>
                          <span>${fmt(t.balanceUsed)}</span>
                          {t.tradeType === "futures" && t.leverage > 1 && (
                            <span
                              style={{
                                marginLeft: 5,
                                fontSize: 11,
                                fontWeight: 700,
                                color: "#5e17eb",
                                background: "#f0ebff",
                                borderRadius: 4,
                                padding: "1px 5px",
                              }}
                            >
                              {t.leverage}x
                            </span>
                          )}
                        </TD>
                        <TD>
                          {moment(t.createdAt).format("M/D/YYYY, h:mm:ss A")}
                        </TD>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Closed Trades ── */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <span
                style={{ fontWeight: 700, fontSize: 14, letterSpacing: 0.5 }}
              >
                CLOSED TRADES
              </span>
              <span
                style={{
                  background: "#5e17eb",
                  color: "#fff",
                  borderRadius: 20,
                  padding: "2px 10px",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {closedTrades.length}
              </span>
              <select
                value={outcomeFilter}
                onChange={(e) => handleOutcomeFilter(e.target.value)}
                style={{
                  marginLeft: "auto",
                  padding: "4px 10px",
                  borderRadius: 8,
                  border: "1px solid #ddd",
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                <option value="">All Outcomes</option>
                <option value="TP Hit">TP Hit</option>
                <option value="SL Hit">SL Hit</option>
                <option value="Closed">Closed</option>
              </select>
            </div>
            <div
              style={{
                overflowX: "auto",
                borderRadius: 10,
                boxShadow: "0 1px 8px rgba(0,0,0,0.08)",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  background: "#fff",
                }}
              >
                <thead>
                  <tr>
                    <TH>Symbol</TH>
                    <TH>Source</TH>
                    <TH>Position</TH>
                    <TH>Entry</TH>
                    <TH>Exit</TH>
                    <TH>Outcome</TH>
                    <TH right>P&L</TH>
                    <TH>Opened</TH>
                    <TH>Closed</TH>
                  </tr>
                </thead>
                <tbody>
                  {closedTrades.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          textAlign: "center",
                          padding: 20,
                          color: "#aaa",
                        }}
                      >
                        No closed trades
                      </td>
                    </tr>
                  ) : (
                    closedTrades.map((t) => (
                      <tr key={t._id} style={{ background: "#fff" }}>
                        <TD>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>
                            {t.coin || t.currency || "—"}
                          </span>
                        </TD>
                        <TD>
                          <span
                            style={{
                              background:
                                t.sourceType === "liveSession"
                                  ? "#e8f4fd"
                                  : t.sourceType === "stock"
                                    ? "#e8f5e9"
                                    : t.sourceType === "forex"
                                      ? "#fff3e0"
                                      : "#f0f0ff",
                              color:
                                t.sourceType === "liveSession"
                                  ? "#1565c0"
                                  : t.sourceType === "stock"
                                    ? "#2e7d32"
                                    : t.sourceType === "forex"
                                      ? "#e65100"
                                      : "#5e17eb",
                              borderRadius: 6,
                              padding: "2px 8px",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {t.sourceType === "liveSession"
                              ? "Live"
                              : t.sourceType === "stock"
                                ? "Stock"
                                : t.sourceType === "forex"
                                  ? "Forex"
                                  : "Crypto"}
                          </span>
                        </TD>
                        <TD>
                          <PositionBadge
                            type={
                              t.positionType ||
                              (t.tradeType === "spot" ? "Spot" : null)
                            }
                          />
                        </TD>
                        <TD>{fmtPrice(t.entryPrice)}</TD>
                        <TD>{t.closedPrice ? fmtPrice(t.closedPrice) : "—"}</TD>
                        <TD>
                          <OutcomeBadge outcome={t.outcome} />
                        </TD>
                        <TD right>
                          {t.pnlUSDT != null ? (
                            <span
                              style={{
                                color: t.pnlUSDT >= 0 ? "#1a7a40" : "#c0392b",
                                fontWeight: 600,
                              }}
                            >
                              {t.pnlUSDT >= 0 ? "+" : ""}
                              {fmt(t.pnlUSDT)} USDT ({fmt(t.roiPercent)}%)
                            </span>
                          ) : (
                            "—"
                          )}
                        </TD>
                        <TD>
                          {moment(t.createdAt).format("M/D/YYYY, h:mm:ss A")}
                        </TD>
                        <TD>
                          {t.resolvedAt
                            ? moment(t.resolvedAt).format("M/D/YYYY, h:mm:ss A")
                            : "—"}
                        </TD>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {closedTotalPages > 1 && (
              <Stack spacing={2} alignItems="center" sx={{ mt: 2, mb: 1 }}>
                <Pagination
                  count={closedTotalPages}
                  page={closedPage}
                  onChange={(_, v) => loadClosedPage(v)}
                  color="primary"
                  size="small"
                />
              </Stack>
            )}
          </div>
        </>
      )}
    </div>
  );
};
